import { ReactNode } from 'react';
import styled from 'styled-components';
import {
  color,
  flexbox,
  grid,
  layout,
  space,
  background,
  border,
  position,
  shadow,
  system,
} from 'styled-system';
import type {
  SpaceProps,
  ColorProps,
  LayoutProps,
  FlexboxProps,
  GridProps,
  BackgroundProps,
  BorderProps,
  PositionProps,
  ShadowProps,
  Theme,
  ResponsiveValue,
} from 'styled-system';
import { S, L, O } from 'ts-toolbelt';

import { TokenType } from '@vimeo/ui/tokens';

type VimeoTheme = TokenType & Theme;

type FlattenColors<T extends Theme> = S.Join<L.Flatten<O.Paths<T['colors']>>, '.'>;

type FlattenSpace<T extends Theme> = T['space'] extends (infer P)[]
  ? ResponsiveValue<P>
  : S.Join<L.Flatten<O.Paths<T['space']>>, '.'>;

interface GapProps<ThemeType extends Theme = VimeoTheme> {
  gap?: ResponsiveValue<string | number, ThemeType> | undefined;
}

export interface BoxPropTypes<T extends Theme = VimeoTheme>
  extends SpaceProps<T, FlattenSpace<T>>,
    ColorProps<T, FlattenColors<T>>,
    LayoutProps<T>,
    FlexboxProps<T>,
    GridProps<T>,
    BackgroundProps<T>,
    BorderProps<T>,
    PositionProps<T>,
    GapProps<T>,
    ShadowProps<T> {
  children?: ReactNode;
}

const gap = system({
  gap: {
    property: 'gap',
  },
});

/**
 * A base component that provides sensible layout properties that map to
 * a defined theme of design tokens.
 */
let Box = styled.div.withConfig({
  shouldForwardProp: (prop) => ['as', 'children'].includes(prop),
})<BoxPropTypes<VimeoTheme>>`
  ${space}
  ${color}
  ${layout}
  ${flexbox}
  ${grid}
  ${background}
  ${border}
  ${position}
  ${shadow}
  ${gap}
`;

export default Box;
